import type { IMentionEditorSurface } from '../mentionEditor/types';
import { isMentionEditorSurface } from '../mentionEditor/types';
import {
  getCaretRectAtOffset,
  getSelectionOffsets,
} from '../mentionEditor/editorDom';

const MIRROR_PROPS = [
  'boxSizing',
  'width',
  'height',
  'overflowX',
  'overflowY',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontSizeAdjust',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'textDecoration',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
  'MozTabSize',
  'whiteSpace',
  'wordWrap',
  'wordBreak',
] as const;

const ANCHOR_POSITIONED_ATTR = 'data-mention-anchor-positioned';

/**
 * 将 textarea 的计算样式克隆到 mirror 元素上
 */
function applyMirrorStyles(mirror: HTMLElement, sourceStyle: CSSStyleDeclaration): void {
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.overflow = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';

  MIRROR_PROPS.forEach((prop) => {
    mirror.style[prop as any] = sourceStyle.getPropertyValue(
      prop.replace(/([A-Z])/g, '-$1').toLowerCase(),
    );
  });
}

/**
 * 获取 textarea 中指定字符索引处相对于页面视口的坐标
 */
export function getCaretCoordinates(
  textarea: HTMLTextAreaElement,
  charIndex: number,
): { left: number; top: number; } {
  const mirror = document.createElement('div');
  const style = window.getComputedStyle(textarea);

  applyMirrorStyles(mirror, style);

  const textBefore = textarea.value.substring(0, charIndex);
  mirror.appendChild(document.createTextNode(textBefore));

  const span = document.createElement('span');
  span.textContent = textarea.value.charAt(charIndex) || '\u200b';
  mirror.appendChild(span);

  document.body.appendChild(mirror);

  const textareaRect = textarea.getBoundingClientRect();
  const spanRect = span.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();

  const offsetLeft = spanRect.left - mirrorRect.left;
  const offsetTop = spanRect.top - mirrorRect.top;

  document.body.removeChild(mirror);

  return {
    left: textareaRect.left + offsetLeft - textarea.scrollLeft,
    top: textareaRect.top + offsetTop - textarea.scrollTop,
  };
}

function isValidCaretClientRect(rect: DOMRect, bounds?: DOMRect): boolean {
  if (!Number.isFinite(rect.top) || !Number.isFinite(rect.left)) return false;
  if (rect.top < -100 || rect.left < -100) return false;
  if (!bounds) return true;
  const margin = 200;
  return (
    rect.top >= bounds.top - margin &&
    rect.top <= bounds.bottom + margin &&
    rect.left >= bounds.left - margin &&
    rect.left <= bounds.right + margin
  );
}

/**
 * MentionEditor：优先用浏览器原生选区（刚输入 @ 后最准确），
 * 再回退到 setSelectionOffsets；均失败则返回 null，避免沿用旧锚点。
 */
function resolveMentionEditorCaretRect(
  root: HTMLElement,
  charIndex: number,
): DOMRect | null {
  const rootRect = root.getBoundingClientRect();
  const sel = window.getSelection();

  if (sel?.rangeCount) {
    const range = sel.getRangeAt(0);
    if (root.contains(range.commonAncestorContainer)) {
      const offsets = getSelectionOffsets(root);
      if (offsets.start === charIndex + 1 || offsets.start === charIndex) {
        const liveRect = range.getBoundingClientRect();
        if (isValidCaretClientRect(liveRect, rootRect)) {
          return liveRect;
        }
      }
    }
  }

  const atRect = getCaretRectAtOffset(root, charIndex);
  if (atRect && isValidCaretClientRect(atRect, rootRect)) {
    return atRect;
  }

  const afterRect = getCaretRectAtOffset(root, charIndex + 1);
  if (afterRect && isValidCaretClientRect(afterRect, rootRect)) {
    return afterRect;
  }

  return null;
}

/** 将虚拟锚点定位到指定视口坐标（fixed，供 Portal 参考） */
export function positionVirtualAnchor(
  anchor: HTMLElement,
  left: number,
  top: number,
  lineHeight: number,
): void {
  anchor.style.position = 'fixed';
  anchor.style.left = `${left}px`;
  anchor.style.top = `${top}px`;
  anchor.style.width = '1px';
  anchor.style.height = `${lineHeight}px`;
  anchor.style.pointerEvents = 'none';
  anchor.style.opacity = '0';
  anchor.style.zIndex = '-1';
  anchor.setAttribute(ANCHOR_POSITIONED_ATTR, 'true');
}

/** 关闭下拉后清除锚点，避免下次打开误用旧坐标 */
export function hideVirtualAnchor(anchor: HTMLElement): void {
  anchor.removeAttribute(ANCHOR_POSITIONED_ATTR);
  anchor.style.position = 'fixed';
  anchor.style.left = '-9999px';
  anchor.style.top = '-9999px';
  anchor.style.width = '1px';
  anchor.style.height = '1px';
  anchor.style.pointerEvents = 'none';
  anchor.style.opacity = '0';
  anchor.style.zIndex = '-1';
}

/** 仅在下拉打开且锚点已成功定位时返回 rect */
export function getVirtualAnchorRect(
  anchor: HTMLElement | null | undefined,
  bounds?: DOMRect,
): DOMRect | null {
  if (!anchor || anchor.getAttribute(ANCHOR_POSITIONED_ATTR) !== 'true') {
    return null;
  }
  const rect = anchor.getBoundingClientRect();
  if (!isValidCaretClientRect(rect, bounds)) {
    return null;
  }
  return rect;
}

/** 按触发符索引更新虚拟锚点位置；失败时隐藏锚点，由 Portal 回退到容器定位 */
export function updateVirtualAnchorAtChar(
  input: HTMLTextAreaElement | IMentionEditorSurface,
  anchor: HTMLElement,
  charIndex: number,
): void {
  if (isMentionEditorSurface(input)) {
    const root = input.getRootElement();
    if (!root) {
      hideVirtualAnchor(anchor);
      return;
    }
    const rect = resolveMentionEditorCaretRect(root, charIndex);
    if (!rect) {
      hideVirtualAnchor(anchor);
      return;
    }
    const style = window.getComputedStyle(root);
    const lineHeight =
      parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
    positionVirtualAnchor(anchor, rect.left, rect.top, lineHeight);
    return;
  }

  const coords = getCaretCoordinates(input, charIndex);
  const style = window.getComputedStyle(input);
  const lineHeight =
    parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
  positionVirtualAnchor(anchor, coords.left, coords.top, lineHeight);
}
